import { hash64, type Key } from "./hashing";

export interface HyperLogLogOptions {
  /**
   * Precision p in [4, 18]. The sketch uses m = 2^p registers and has a
   * relative standard error of about 1.04 / sqrt(m).
   */
  precision?: number;
  seed?: number;
}

/**
 * HyperLogLog estimates the number of *distinct* items in a stream — the
 * cardinality — using a fixed, tiny amount of memory no matter how many items
 * flow through it.
 *
 * The idea: hash each item to 64 bits. Use the top p bits to pick one of
 * m = 2^p registers, and in that register remember the largest number of
 * leading zeros seen in the remaining bits. Long runs of leading zeros are
 * rare, so the maximum run length across the registers is a fingerprint of how
 * many distinct values were hashed. Averaging across registers (with a harmonic
 * mean) turns that into a cardinality estimate with ~1.04/sqrt(m) error.
 */
export class HyperLogLog {
  readonly precision: number;
  readonly m: number;
  readonly seed: number;
  readonly registers: Uint8Array;
  private readonly rhoBits: number;

  constructor(options: HyperLogLogOptions = {}) {
    const { precision = 14, seed = 0 } = options;
    if (!Number.isInteger(precision) || precision < 4 || precision > 18)
      throw new RangeError("precision must be an integer in [4, 18]");
    this.precision = precision;
    this.m = 1 << precision;
    this.seed = seed;
    this.rhoBits = 64 - precision;
    this.registers = new Uint8Array(this.m);
  }

  /** Register index and leading-zero run (rho) a key maps to. */
  trace(key: Key): { index: number; rho: number } {
    const x = hash64(key, this.seed);
    const index = Number(x >> BigInt(this.rhoBits));
    const w = x & ((1n << BigInt(this.rhoBits)) - 1n);
    return { index, rho: this.rho(w) };
  }

  private rho(w: bigint): number {
    if (w === 0n) return this.rhoBits + 1;
    // (bits in the window) - (position of the highest set bit) gives the
    // 1-indexed position of the leftmost 1.
    return this.rhoBits - (w.toString(2).length - 1);
  }

  add(key: Key): void {
    const x = hash64(key, this.seed);
    const index = Number(x >> BigInt(this.rhoBits));
    const w = x & ((1n << BigInt(this.rhoBits)) - 1n);
    const r = this.rho(w);
    if (r > this.registers[index]) this.registers[index] = r;
  }

  /** Estimated number of distinct items added so far. */
  count(): number {
    return Math.round(this.rawEstimate());
  }

  private rawEstimate(): number {
    const m = this.m;
    let sum = 0;
    let zeros = 0;
    for (let j = 0; j < m; j++) {
      const r = this.registers[j];
      sum += 2 ** -r;
      if (r === 0) zeros++;
    }

    let estimate = (alpha(m) * m * m) / sum;

    // Small-range correction: for low cardinalities the harmonic-mean estimator
    // is biased, so switch to linear counting while empty registers remain.
    if (estimate <= 2.5 * m && zeros > 0) {
      estimate = m * Math.log(m / zeros);
    }
    // No large-range (2^32) correction is needed: we hash to 64 bits, so the
    // estimator does not saturate in any realistic range.
    return estimate;
  }

  /** Relative standard error of the estimate, ≈ 1.04 / sqrt(m). */
  standardError(): number {
    return 1.04 / Math.sqrt(this.m);
  }

  /** Merge another sketch (same precision) into this one — a set union. */
  merge(other: HyperLogLog): void {
    if (other.precision !== this.precision)
      throw new Error("cannot merge HyperLogLog sketches of different precision");
    for (let j = 0; j < this.m; j++) {
      if (other.registers[j] > this.registers[j]) this.registers[j] = other.registers[j];
    }
  }

  /** Bytes of register memory this sketch occupies. */
  memoryBytes(): number {
    return this.m; // one byte per register
  }
}

function alpha(m: number): number {
  switch (m) {
    case 16:
      return 0.673;
    case 32:
      return 0.697;
    case 64:
      return 0.709;
    default:
      return 0.7213 / (1 + 1.079 / m);
  }
}
